import React, { useState, useEffect } from "react";
import { Row, Col, Space, Button, Typography, message, Switch, DatePicker, Modal, Divider, Tooltip } from "antd";
import { CalendarFilled, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import DocumentCard from "./DocumentCard";
import DocumentForm from "./DocumentForm";
import Subtitle from "./Subtitle";
import { PlusOutlined } from "@ant-design/icons";
import { useAuth } from "../../layout/auth";
import moment from "moment";

export default function Documents(props) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [documents, setDocuments] = useState([]);
  const { id, verification, title, list, setVisits, user, usage, setVerification } = props;

  const auth = useAuth();

  useEffect(() => {
    fetchDocuments();
  }, [id, list]);

  const fetchDocuments = () => {
    const tiposParam = list && list.length > 0 ? `&tipos[]=${list.join("&tipos[]=")}` : "";

    axios
      .get(`${process.env.REACT_APP_API_URL}/documentos/${id}?${tiposParam}`)
      .then((response) => {
        setDocuments(response.data.documents);
      })
      .catch((error) => {
        console.error("Error fetching documents:", error);
        message.error("Failed to fetch documents.");
      });
  };

  const handlePapeleriaRecibidaChange = (value, VerificacionID) => {
    const requestData = {
      PapeleriaRecibida: value ? 1 : 0,
      FechaPapeleria: value ? moment().format("YYYY-MM-DD") : null,
    };

    axios
      .put(`${process.env.REACT_APP_API_URL}/verificaciones/papeleria/${VerificacionID}`, requestData)
      .then((response) => {
        message.success("Papeleria marcada como recibida exitosamente");
      })
      .catch((error) => {
        message.error("Error marcando papeleria como recibida");
        console.log(error);
      });

    axios
      .post(`${process.env.REACT_APP_API_URL}/notificaciones`, {
        UsuarioID: verification.UsuarioID,
        Mensaje: `La verificación ${verification.ControlInterno} tiene su papelería completa.`,
        Estado: 0,
        TargetURL: `/verificaciones/${verification.VerificacionID}`,
      })
      .then(() => {
        message.success("Notificación enviada al usuario");
      })
      .catch((err) => {
        console.error("Error enviando notificación al usuario:", err);
      });
  };

  const handleDateEdit = (dateType, currentDate) => {
    let selectedDate = null;
    const isStartDate = dateType === "start";
    const title = isStartDate ? "Fecha de inicio de revisión" : "Fecha de término de revisión";
    const field = isStartDate ? "FechaRevision1" : "FechaRevision2";

    Modal.confirm({
      title: `Editar ${title}`,
      content: (
        <div style={{ padding: "16px 0" }}>
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            defaultValue={currentDate ? moment(currentDate, "YYYY-MM-DD") : null}
            onChange={(date) => {
              selectedDate = date;
            }}
            placeholder="Seleccionar fecha"
          />
        </div>
      ),
      okText: "Guardar",
      cancelText: "Cancelar",
      onOk: async () => {
        if (selectedDate) {
          const verificationData = {
            ...verification,
            [field]: selectedDate.format("YYYY-MM-DD"),
          };

          try {
            await axios.put(`${process.env.REACT_APP_API_URL}/verificaciones/${verification.VerificacionID}`, verificationData);
            message.success(`${title} actualizada`);
            setVerification(verificationData);
            await axios.post(`${process.env.REACT_APP_API_URL}/notificaciones/directores`, {
              Mensaje: `El proyecto de la verificación ${verification.ControlInterno} ya fue revisado con fecha ${selectedDate.format(
                "DD/MM/YYYY"
              )}.`,
              Estado: 0,
              TargetURL: `/verificaciones/${verification.VerificacionID}`,
            });
            message.success("Notificación enviada a directores");
          } catch (err) {
            console.error("Error al actualizar o notificar:", err);
            message.error("Error al actualizar fecha");
          }
        } else {
          message.warning("Seleccione una fecha.");
        }
      },
    });
  };

  const handleAddDocument = () => {
    setIsModalVisible(true);
  };

  return (
    <div>
      <Subtitle title={title} marginTop={0} />

      <Row gutter={[16, 16]}>
        <Col span={12}>
          {auth?.Rol === "director" && usage === "paperwork" && (
            <Switch
              checkedChildren="Se entregó papelería"
              unCheckedChildren="Marcar papelería como entregada"
              onChange={(value) => handlePapeleriaRecibidaChange(value, id)}
              defaultChecked={verification?.PapeleriaRecibida === 1}
            />
          )}
        </Col>
        <Col span={12} style={{ textAlign: "right" }}>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddDocument}>
              Agregar documento
            </Button>
          </Space>
        </Col>
      </Row>

      <Divider />

      {usage === "revision" && (
        <div style={{ marginBottom: "16px" }}>
          <Space size="large">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography.Text strong>
                <CalendarFilled style={{ marginRight: "4px", color: "#e76f00" }} />
                Inicio:
              </Typography.Text>
              <Typography.Text>
                {verification?.FechaRevision1 ? moment(verification.FechaRevision1, "YYYY-MM-DD").format("DD/MM/YYYY") : "No registrada"}
              </Typography.Text>
              <Tooltip
                title={
                  auth?.Rol !== "director" && verification?.FolioSediver !== null
                    ? "Sólo los directivos pueden editar esta fecha"
                    : "Editar fecha de inicio"
                }
              >
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  disabled={auth?.Rol !== "director" && verification?.Status >= 3}
                  onClick={() => handleDateEdit("start", verification?.FechaRevision1)}
                />
              </Tooltip>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography.Text strong>
                <CalendarFilled style={{ marginRight: "4px", color: "#e76f00" }} />
                Término:
              </Typography.Text>
              <Typography.Text>
                {verification?.FechaRevision2 ? moment(verification.FechaRevision2, "YYYY-MM-DD").format("DD/MM/YYYY") : "No registrada"}
              </Typography.Text>
              <Tooltip
                title={
                  auth?.Rol !== "director" && verification?.FolioSediver !== null
                    ? "Sólo los directivos pueden editar esta fecha"
                    : "Editar fecha de término"
                }
              >
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  disabled={auth?.Rol !== "director" && verification?.Status >= 3}
                  onClick={() => handleDateEdit("end", verification?.FechaRevision2)}
                />
              </Tooltip>
            </div>
          </Space>
        </div>
      )}

      {/* Document List (Cards) */}
      <Row gutter={[16, 16]}>
        {documents.map((doc) => (
          <DocumentCard key={doc.DocumentoID} doc={doc} fetchDocuments={fetchDocuments} verification={verification} user={user} />
        ))}
      </Row>

      {/* Modal for Upload */}
      <DocumentForm
        visible={isModalVisible}
        setVisible={setIsModalVisible}
        id={id}
        fetchDocuments={fetchDocuments}
        list={list}
        setVisits={setVisits}
        verification={verification}
      />
    </div>
  );
}
